'use strict';

/**
 * PlansheetPromptProvider
 * -----------------------
 * Adapts plansheet's Prompt/WordList/GeneratedPrompt REST providers to the
 * data seam pict-section-prompteditor expects (its PromptDataProvider
 * interface), the same way PlansheetCommentProvider adapts the comments
 * section. The editor calls neutral methods keyed by module Keys; this maps
 * them to server rows and back:
 *
 *   module Key            server column
 *   Prompt.Key        <-> Prompt.PromptKey         (IDPrompt cached per Key)
 *   WordList.Key      <-> WordList.WordListKey
 *   Generated.Key     <-> GeneratedPrompt.GeneratedKey
 *
 * On every prompt save the provider also compiles the assembled Source (the
 * module's PromptCompiler, against the current shape cascade) so the server's
 * version capture snapshots a complete, versionable document - that Source is
 * what history, the shared library, and pinned attachment versions show.
 *
 * Server-owned fields ride on Meta._Plansheet (IDPrompt, PromptNumber,
 * Visibility, Origin, PostedDate) so page chrome can read them off the
 * module's own records; the provider strips _Plansheet before writing Meta
 * back.
 */

const libPromptEditor = require('pict-section-prompteditor');

class PlansheetPromptProvider extends libPromptEditor.PromptDataProvider
{
	constructor(pPict)
	{
		super({});
		this.pict = pPict;
		this._types = null;
		this._promptIDByKey = {};
		this._promptKeyByID = {};
		this._promptRowByKey = {};
		this._promptSaveChain = {};
		this._wordListIDByKey = {};
		this._generatedIDByKey = {};
	}

	_promptAPI() { return this.pict.providers.PromptAPI; }
	_wordListAPI() { return this.pict.providers.WordListAPI; }
	_generatedAPI() { return this.pict.providers.GeneratedPromptAPI; }
	_unwrap(pResult) { return (pResult && pResult.Record) ? pResult.Record : pResult; }

	/** The shape cascade the compiler resolves TypeKeys against (set by the host page). */
	setPromptTypes(pTypes)
	{
		this._types = Array.isArray(pTypes) && pTypes.length ? pTypes : null;
	}

	_ts(pDate)
	{
		if (!pDate) { return null; }
		let tmpValue = (typeof pDate === 'number') ? pDate : Date.parse(pDate);
		return isNaN(tmpValue) ? null : tmpValue;
	}

	_parse(pJSON, pDefault)
	{
		if (pJSON == null || pJSON === '') { return pDefault; }
		if (typeof pJSON !== 'string') { return pJSON; }
		try { return JSON.parse(pJSON); } catch (pError) { return pDefault; }
	}

	_author(pIDUser)
	{
		let tmpKey = pIDUser || 0;
		let tmpAuth = (this.pict.AppData.Auth && this.pict.AppData.Auth.User) || null;
		if (tmpAuth && String(tmpAuth.IDUser) === String(tmpKey))
		{
			return { Key: tmpKey, Name: tmpAuth.DisplayName || tmpAuth.Email || 'You' };
		}
		let tmpTeam = this.pict.AppData.Team || {};
		let tmpPeople = [].concat(tmpTeam.Roster || [], tmpTeam.Members || []);
		let tmpHit = tmpPeople.find((pPerson) => String(pPerson.IDUser) === String(tmpKey));
		if (tmpHit)
		{
			let tmpName = tmpHit.DisplayName
				|| ([tmpHit.NameFirst, tmpHit.NameLast].filter(Boolean).join(' ') || '').trim()
				|| tmpHit.Email || ('User ' + tmpKey);
			return { Key: tmpKey, Name: tmpName };
		}
		return { Key: tmpKey, Name: tmpKey ? ('User ' + tmpKey) : 'Someone' };
	}

	_compileSource(pRecord)
	{
		try
		{
			let tmpTypes = this._types || libPromptEditor.DefaultPromptTypes;
			let tmpType = libPromptEditor.getPromptType(tmpTypes, pRecord.TypeKey);
			return libPromptEditor.PromptCompiler.assembleSource(pRecord, tmpType, {});
		}
		catch (pError)
		{
			return '';
		}
	}

	// ---- row <-> module record transforms ------------------------------------

	_toPrompt(pRow)
	{
		let tmpKey = pRow.PromptKey || ('pr-id-' + pRow.IDPrompt);
		this._promptIDByKey[tmpKey] = pRow.IDPrompt;
		this._promptKeyByID[pRow.IDPrompt] = tmpKey;
		// The merge base for partial patches (the editor saves Title, TypeKey, and
		// Segments as separate debounced patches; merging against a cached row
		// instead of a re-fetch keeps concurrent saves from resurrecting stale state).
		this._promptRowByKey[tmpKey] = pRow;
		let tmpMeta = this._parse(pRow.Meta, {}) || {};
		tmpMeta._Plansheet =
		{
			IDPrompt: pRow.IDPrompt,
			PromptNumber: pRow.PromptNumber || 0,
			Visibility: pRow.Visibility || 'Private',
			Origin: pRow.Origin || 'Crafted',
			PostedDate: pRow.PostedDate || null
		};
		return {
			Key: tmpKey,
			Title: pRow.Title || '',
			TypeKey: pRow.TypeKey || 'freeform',
			Segments: this._parse(pRow.SegmentsJSON, {}) || {},
			IncludeSegmentHeadings: !(Number(pRow.IncludeSegmentHeadings) === 0),
			Author: this._author(pRow.IDUser || pRow.CreatingIDUser),
			Meta: tmpMeta,
			CreatedAt: this._ts(pRow.CreateDate) || 0,
			UpdatedAt: this._ts(pRow.UpdateDate) || 0
		};
	}

	_toWordList(pRow)
	{
		let tmpKey = pRow.WordListKey || ('wl-id-' + pRow.IDWordList);
		this._wordListIDByKey[tmpKey] = pRow.IDWordList;
		return {
			Key: tmpKey,
			Name: pRow.Name || '',
			Entries: libPromptEditor.normalizeEntries(this._parse(pRow.EntriesJSON, [])),
			CreatedAt: this._ts(pRow.CreateDate) || 0,
			UpdatedAt: this._ts(pRow.UpdateDate) || 0
		};
	}

	_toGenerated(pRow)
	{
		let tmpKey = pRow.GeneratedKey || ('gen-id-' + pRow.IDGeneratedPrompt);
		this._generatedIDByKey[tmpKey] = pRow.IDGeneratedPrompt;
		return {
			Key: tmpKey,
			PromptKey: this._promptKeyByID[pRow.IDPrompt] || ('pr-id-' + pRow.IDPrompt),
			PromptTitle: pRow.PromptTitle || '',
			TypeKey: pRow.TypeKey || '',
			Markdown: pRow.Markdown || '',
			Sequence: Number(pRow.Sequence) || 0,
			Author: this._author(pRow.IDUser || pRow.CreatingIDUser),
			GeneratedAt: this._ts(pRow.CreateDate) || 0
		};
	}

	_metaForWrite(pMeta)
	{
		let tmpMeta = Object.assign({}, pMeta || {});
		delete tmpMeta._Plansheet;
		return JSON.stringify(tmpMeta);
	}

	// ---- word lists -----------------------------------------------------------

	listWordLists()
	{
		return this._wordListAPI().list()
			.then((pRows) => (pRows || []).map((pRow) => this._toWordList(pRow))
				.sort((pA, pB) => String(pA.Name).localeCompare(String(pB.Name))));
	}

	createWordList(pDraft)
	{
		let tmpDraft = pDraft || {};
		let tmpName = String(tmpDraft.Name || '').trim();
		if (!tmpName) { return Promise.reject(new Error('A word list needs a Name.')); }
		return this._wordListAPI().create(
		{
			Name: tmpName.slice(0, 128),
			EntriesJSON: JSON.stringify(libPromptEditor.normalizeEntries(tmpDraft.Entries)),
			Visibility: 'Private'
		}).then((pResult) => this._toWordList(this._unwrap(pResult)));
	}

	updateWordList(pKey, pPatch)
	{
		let tmpID = this._wordListIDByKey[pKey];
		if (!tmpID) { return Promise.reject(new Error('No word list with Key ' + pKey)); }
		let tmpPatch = pPatch || {};
		let tmpRow = {};
		if (typeof tmpPatch.Name !== 'undefined')
		{
			let tmpName = String(tmpPatch.Name || '').trim();
			if (!tmpName) { return Promise.reject(new Error('A word list needs a Name.')); }
			tmpRow.Name = tmpName.slice(0, 128);
		}
		if (typeof tmpPatch.Entries !== 'undefined')
		{
			tmpRow.EntriesJSON = JSON.stringify(libPromptEditor.normalizeEntries(tmpPatch.Entries));
		}
		return this._wordListAPI().update(tmpID, tmpRow)
			.then((pResult) => this._toWordList(this._unwrap(pResult)));
	}

	deleteWordList(pKey)
	{
		let tmpID = this._wordListIDByKey[pKey];
		if (!tmpID) { return Promise.resolve(); }
		return this._wordListAPI().remove(tmpID).then(() => { delete this._wordListIDByKey[pKey]; });
	}

	// ---- prompts ----------------------------------------------------------------

	listPrompts()
	{
		return this._promptAPI().mine()
			.then((pRows) => (pRows || []).map((pRow) => this._toPrompt(pRow)));
	}

	createPrompt(pDraft)
	{
		let tmpDraft = pDraft || {};
		if (!tmpDraft.TypeKey) { return Promise.reject(new Error('A prompt needs a TypeKey.')); }
		let tmpRecord =
		{
			Title: String(tmpDraft.Title || 'Untitled prompt'),
			TypeKey: String(tmpDraft.TypeKey),
			Segments: (tmpDraft.Segments && typeof tmpDraft.Segments === 'object') ? tmpDraft.Segments : {},
			IncludeSegmentHeadings: tmpDraft.IncludeSegmentHeadings !== false
		};
		return this._promptAPI().create(
		{
			Title: tmpRecord.Title.slice(0, 200),
			TypeKey: tmpRecord.TypeKey,
			SegmentsJSON: JSON.stringify(tmpRecord.Segments),
			IncludeSegmentHeadings: tmpRecord.IncludeSegmentHeadings ? 1 : 0,
			Source: this._compileSource(tmpRecord),
			Meta: this._metaForWrite(tmpDraft.Meta)
		}).then((pResult) => this._toPrompt(this._unwrap(pResult)));
	}

	updatePrompt(pKey, pPatch)
	{
		let tmpID = this._promptIDByKey[pKey];
		if (!tmpID) { return Promise.reject(new Error('No prompt with Key ' + pKey)); }
		let tmpPatch = pPatch || {};
		// The editor fires Title / TypeKey / Segments as separate debounced
		// patches that can be in flight together. Serialize saves per prompt and
		// merge each patch against the cached last-known row, so a title-only
		// save can never write back stale segments (and the compiled Source stays
		// complete for version capture).
		let tmpChain = (this._promptSaveChain[pKey] || Promise.resolve()).then(() =>
		{
			let tmpRow = this._promptRowByKey[pKey] || {};
			let tmpMerged =
			{
				Title: (typeof tmpPatch.Title !== 'undefined') ? String(tmpPatch.Title) : (tmpRow.Title || ''),
				TypeKey: (typeof tmpPatch.TypeKey !== 'undefined') ? String(tmpPatch.TypeKey) : (tmpRow.TypeKey || 'freeform'),
				Segments: (typeof tmpPatch.Segments !== 'undefined') ? (tmpPatch.Segments || {}) : (this._parse(tmpRow.SegmentsJSON, {}) || {}),
				IncludeSegmentHeadings: (typeof tmpPatch.IncludeSegmentHeadings !== 'undefined')
					? (tmpPatch.IncludeSegmentHeadings !== false)
					: !(Number(tmpRow.IncludeSegmentHeadings) === 0)
			};
			let tmpUpdate =
			{
				Title: tmpMerged.Title.slice(0, 200),
				TypeKey: tmpMerged.TypeKey,
				SegmentsJSON: JSON.stringify(tmpMerged.Segments),
				IncludeSegmentHeadings: tmpMerged.IncludeSegmentHeadings ? 1 : 0,
				Source: this._compileSource(tmpMerged)
			};
			if (typeof tmpPatch.Meta !== 'undefined') { tmpUpdate.Meta = this._metaForWrite(tmpPatch.Meta); }
			return this._promptAPI().update(tmpID, tmpUpdate)
				.then((pResult) => this._toPrompt(this._unwrap(pResult)));
		});
		// The chain must survive a failed save (the next patch still runs).
		this._promptSaveChain[pKey] = tmpChain.catch(() => { });
		return tmpChain;
	}

	deletePrompt(pKey)
	{
		let tmpID = this._promptIDByKey[pKey];
		if (!tmpID) { return Promise.resolve(); }
		// Soft-delete the prompt, then its generations (mirrors the in-memory
		// provider, which drops a prompt's generated output alongside it).
		return this._promptAPI().remove(tmpID)
			.then(() => this.clearGenerated(pKey))
			.then(() => { delete this._promptIDByKey[pKey]; });
	}

	// ---- generated output ---------------------------------------------------------

	listGenerated(pPromptKey)
	{
		return this._generatedAPI().list()
			.then((pRows) =>
			{
				let tmpGenerated = (pRows || []).map((pRow) => this._toGenerated(pRow))
					.filter((pGen) => !pPromptKey || pGen.PromptKey === pPromptKey);
				let tmpLatestByPrompt = {};
				tmpGenerated.forEach((pGen) =>
				{
					let tmpStamp = pGen.GeneratedAt || 0;
					if (!(pGen.PromptKey in tmpLatestByPrompt) || tmpStamp > tmpLatestByPrompt[pGen.PromptKey]) { tmpLatestByPrompt[pGen.PromptKey] = tmpStamp; }
				});
				tmpGenerated.sort((pA, pB) =>
					(tmpLatestByPrompt[pB.PromptKey] - tmpLatestByPrompt[pA.PromptKey])
					|| String(pA.PromptKey).localeCompare(String(pB.PromptKey))
					|| (pA.Sequence || 0) - (pB.Sequence || 0)
					|| (pA.GeneratedAt || 0) - (pB.GeneratedAt || 0));
				return tmpGenerated;
			});
	}

	createGenerated(pDraft)
	{
		let tmpDraft = pDraft || {};
		let tmpIDPrompt = this._promptIDByKey[tmpDraft.PromptKey];
		if (!tmpIDPrompt) { return Promise.reject(new Error('Generated output needs a PromptKey.')); }
		return this._generatedAPI().create(
		{
			IDPrompt: tmpIDPrompt,
			PromptTitle: String(tmpDraft.PromptTitle || '').slice(0, 200),
			TypeKey: String(tmpDraft.TypeKey || ''),
			Markdown: String(tmpDraft.Markdown || ''),
			Sequence: Number(tmpDraft.Sequence) || 0
		}).then((pResult) => this._toGenerated(this._unwrap(pResult)));
	}

	deleteGenerated(pKey)
	{
		let tmpID = this._generatedIDByKey[pKey];
		if (!tmpID) { return Promise.resolve(); }
		return this._generatedAPI().remove(tmpID).then(() => { delete this._generatedIDByKey[pKey]; });
	}

	clearGenerated(pPromptKey)
	{
		return this.listGenerated(pPromptKey).then((pGenerated) =>
		{
			let fNext = (pIndex) =>
			{
				if (pIndex >= pGenerated.length) { return Promise.resolve(); }
				return this.deleteGenerated(pGenerated[pIndex].Key).then(() => fNext(pIndex + 1));
			};
			return fNext(0);
		});
	}
}

module.exports = PlansheetPromptProvider;
